export type PartySyncKind = "delivery" | "consignee";

export type PartySyncInput = {
  locationKey: string;
  contactKey: string;
  sameAsDelivery?: boolean;
  sameAsConsignee?: boolean;
  allowLocationInference?: boolean;
};

export const normalizePartyText = (value?: string | null): string => (value ?? "").trim();

export const partyContactKey = (
  name?: string | null,
  phone?: string | null,
  email?: string | null,
): string => {
  const parts = [normalizePartyText(name), normalizePartyText(phone), normalizePartyText(email)];
  return parts.some(Boolean) ? parts.join("|") : "";
};

const keysMatch = (left: string, right: string): boolean => Boolean(left && right && left === right);

const matchesByLocationAndContact = (left: PartySyncInput, right: PartySyncInput): boolean =>
  left.allowLocationInference !== false &&
  keysMatch(left.locationKey, right.locationKey) &&
  keysMatch(left.contactKey, right.contactKey);

export const resolvePartySyncKind = (
  party: PartySyncInput,
  delivery: PartySyncInput,
  options?: { role?: "consignee" | "notify"; consignee?: PartySyncInput },
): PartySyncKind | null => {
  const matchesDelivery = Boolean(party.sameAsDelivery) || matchesByLocationAndContact(party, delivery);
  if (options?.role !== "notify") {
    return matchesDelivery ? "delivery" : null;
  }

  const { consignee } = options;
  const consigneeMatchesDelivery = consignee
    ? resolvePartySyncKind(consignee, delivery) === "delivery"
    : false;
  const matchesConsignee =
    Boolean(party.sameAsConsignee) || Boolean(consignee && matchesByLocationAndContact(party, consignee));

  if (matchesDelivery || (matchesConsignee && consigneeMatchesDelivery)) return "delivery";
  if (matchesConsignee) return "consignee";
  return null;
};
